import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router-dom';
import { Box, Inscription, RoundDoneIcon } from '@common-fe/common-fe';
import _ from 'lodash';

import ROUTES from '@/common/routes';
import { STEPS } from '@/modules/user/components/SignIn/signIn.types';
import { whiteLabelingConfig } from '@/utils/whiteLabeling';
import { THEMES } from '@/utils/whiteLabeling/whiteLabelingConfig';

import { EMAIL_FIELD_KEY } from '../ForgotUsername/ForgotUsername';

import useInitialStepFromUrl from './useInitialStepFromUrl';


const USER_ENTITY_NAME = 'forgotUsername';
export const FORGOT_SUBTITLE =
  'Please enter the username associated with your account and a code will be emailed to you.';

const useSignInStepper = () => {
  const [step, setStep] = useState<STEPS>(STEPS.SIGN_IN);
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const { t } = useTranslation();
  useInitialStepFromUrl(setStep);

  const isChangePasswordMode = useRouteMatch().path === ROUTES.CHANGE_PASSWORD;

  const defaultTitleText = useMemo(() => {
    if (whiteLabelingConfig?.key === THEMES.rippling) {
      return t('Sign in to Elevate');
    }
    if (whiteLabelingConfig?.key === THEMES.gusto) {
      return t('Sign in to Gusto to continue');
    }
    return t('Welcome!');
  }, [t]);

  const defaultSubtitleText = useMemo(() => {
    if (whiteLabelingConfig?.key === THEMES.rippling) {
      return t('Elevate uses single sign-on via your Rippling account.');
    }
    if (whiteLabelingConfig?.key === THEMES.gusto) {
      return t(
        'To access your pre-tax benefit accounts in the Benefits Card Hub powered by Elevate, sign to your Gusto account.'
      );
    }
    return '';
  }, [t]);

  const title = useMemo(() => {
    if (isChangePasswordMode) {
      return t('Change Password');
    }
    if (step === STEPS.FORGOT_PASSWORD) {
      return t('Forgot Password?');
    }
    if (step === STEPS.FORGOT_USERNAME) {
      return t('Forgot Username?');
    }
    if (step === STEPS.FORGOT_USERNAME_SUCCESS) {
      return (
        <Box align="center">
          <RoundDoneIcon />
          <Box margin={{ top: 'spacing24', bottom: 'spacing4' }}>
            <Inscription weight="bold" textAlign="center" size="2xl" wordBreak="break-all" lineHeight="32px">
              {email}
            </Inscription>
          </Box>
        </Box>
      );
    }
    if (step === STEPS.NEW_PASSWORD) {
      return '';
    }
    if (step === STEPS.FORGOT_PASSWORD_SUCCESS) {
      return (
        <Box align="center">
          <RoundDoneIcon />
          <Inscription margin={{ top: 'spacing8' }} weight="bold" size="2xl" lineHeight="32px">
            {t('Password Updated')}
          </Inscription>
        </Box>
      );
    }
    return defaultTitleText;
  }, [isChangePasswordMode, step, defaultTitleText, t, email]);

  const handlePreviousStep = useCallback(() => {
    if (step === STEPS.FORGOT_PASSWORD) {
      setStep(STEPS.SIGN_IN);
    } else if (step === STEPS.FORGOT_USERNAME) {
      setStep(STEPS.SIGN_IN);
    } else if (step === STEPS.NEW_PASSWORD) {
      setStep(STEPS.FORGOT_PASSWORD);
    }
  }, [step]);

  const isBackButton = useMemo(
    () => step === STEPS.FORGOT_PASSWORD || step === STEPS.FORGOT_USERNAME || step === STEPS.NEW_PASSWORD,
    [step]
  );

  const subtitle = useMemo(() => {
    if (step === STEPS.FORGOT_PASSWORD) {
      return t('Please enter the username associated with your account and a code will be emailed to you.');
    }
    if (step === STEPS.FORGOT_USERNAME) {
      return t(
        'Please enter the email address associated with your account and a code will be emailed to you.'
      );
    }
    if (step === STEPS.FORGOT_USERNAME_SUCCESS) {
      return t('If the email is on file, you will receive an email with your username');
    }
    if (step === STEPS.NEW_PASSWORD) {
      return '';
    }
    if (step === STEPS.FORGOT_PASSWORD_SUCCESS) {
      return t('You can now sign in with your new password.');
    }
    return defaultSubtitleText;
  }, [defaultSubtitleText, step, t]);

  const handleNextStep = useCallback(
    async (value?: object) => {
      switch (step) {
        case STEPS.FORGOT_PASSWORD: {
          const currentUsername: string = _.get(value, USER_ENTITY_NAME, '');
          setUsername(currentUsername);
          break;
        }
        case STEPS.NEW_PASSWORD:
          setStep(STEPS.FORGOT_PASSWORD_SUCCESS);
          break;
        case STEPS.FORGOT_USERNAME_SUCCESS:
          setStep(STEPS.SIGN_IN);
          break;
        case STEPS.FORGOT_USERNAME: {
          const userEmail: string = _.get(value, EMAIL_FIELD_KEY, '');
          setStep(STEPS.FORGOT_USERNAME_SUCCESS);
          setEmail(userEmail);
          break;
        }
        case STEPS.FORGOT_PASSWORD_SUCCESS: {
          setStep(STEPS.SIGN_IN);
          break;
        }
        default:
          setStep(STEPS.FORGOT_PASSWORD);
      }
    },
    [step]
  );

  return {
    step,
    username,
    title,
    subtitle,
    isBackButton,
    isChangePasswordMode,
    handlePreviousStep,
    handleNextStep,
    handleForgotUsername: () => setStep(STEPS.FORGOT_USERNAME),
  };
};

export default useSignInStepper;
